/**
 * WizardChunking Component
 * Step 3: Configure chunking strategy
 */
import React, { useState, useMemo, useEffect } from 'react';
import { CHUNKING_STRATEGIES } from './hooks/useWizardState';

// Token estimation: ~4 characters per token
const CHARS_PER_TOKEN = 4;

function estimateTokens(text) {
  return Math.ceil((text?.length || 0) / CHARS_PER_TOKEN);
}

function ChunkPreview({ chunk, index, isExpanded, onToggle }) {
  const tokens = estimateTokens(chunk.text);
  
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => onToggle(index)}
        className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 theme-transition"
      >
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 px-2 py-0.5 rounded">
            #{index + 1}
          </span>
          <span className="text-gray-800 dark:text-gray-200 font-medium">
            {chunk.label}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            ~{tokens.toLocaleString()} Tokens
          </span>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      
      {isExpanded && (
        <div className="p-4 bg-white dark:bg-gray-900/30 max-h-48 overflow-y-auto">
          <pre className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap font-mono">
            {chunk.text.substring(0, 1000)}
            {chunk.text.length > 1000 && (
              <span className="text-gray-400 italic">... ({chunk.text.length - 1000} weitere Zeichen)</span>
            )}
          </pre>
        </div>
      )}
    </div>
  );
}

function WizardChunking({
  files,
  chunkingStrategy,
  blockSize,
  onStrategyChange,
  onBlockSizeChange,
  onGenerateChunks,
  chunks,
  onPrev,
  onNext
}) {
  const [expandedChunk, setExpandedChunk] = useState(null);
  const [localBlockSize, setLocalBlockSize] = useState(blockSize);
  
  // Calculate total selected content
  const selectedContent = useMemo(() => {
    let totalText = '';
    let totalPages = 0;
    
    files.forEach(file => {
      file.pages.forEach(page => {
        if (page.selected) {
          totalText += page.text + '\n\n';
          totalPages++;
        }
      });
    });
    
    return {
      text: totalText,
      pages: totalPages,
      tokens: estimateTokens(totalText)
    };
  }, [files]);
  
  // Generate chunks when strategy changes
  useEffect(() => {
    onGenerateChunks();
  }, [chunkingStrategy, blockSize]);
  
  // Strategy info
  const strategies = [
    {
      id: CHUNKING_STRATEGIES.COMPLETE,
      label: 'Komplett',
      description: 'Alle Seiten als ein Block. Ideal für kurze Dokumente.',
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>,
      recommended: selectedContent.tokens < 8000
    },
    {
      id: CHUNKING_STRATEGIES.PER_PAGE,
      label: 'Pro Seite',
      description: 'Jede Seite einzeln verarbeiten. Gut für strukturierte Inhalte.',
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
      recommended: selectedContent.pages > 5 && selectedContent.pages < 30
    },
    {
      id: CHUNKING_STRATEGIES.BLOCKS,
      label: 'Blöcke',
      description: `Seiten in Gruppen von ${localBlockSize} zusammenfassen.`,
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>,
      recommended: selectedContent.pages >= 30
    }
  ];
  
  const handleToggleExpand = (index) => {
    setExpandedChunk(expandedChunk === index ? null : index);
  };
  
  const handleBlockSizeChange = (value) => {
    setLocalBlockSize(value);
    onBlockSizeChange(value);
  };
  
  return (
    <div className="space-y-6">
      <div className="text-center mb-4">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
          Chunking-Strategie
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Wie soll der Inhalt für die Verarbeitung aufgeteilt werden?
        </p>
      </div>
      
      {/* Content Summary */}
      <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
        <div className="text-center">
          <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
            {selectedContent.pages}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Seiten</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
            {selectedContent.text.length.toLocaleString()}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Zeichen</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
            ~{selectedContent.tokens.toLocaleString()}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Tokens (geschätzt)</div>
        </div>
      </div>
      
      {/* Strategy Selection */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {strategies.map(strategy => (
          <button
            key={strategy.id}
            onClick={() => onStrategyChange(strategy.id)}
            className={`
              relative p-4 rounded-lg border-2 text-left transition-all
              ${chunkingStrategy === strategy.id
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
              }
            `}
          >
            {strategy.recommended && (
              <span className="absolute -top-2 -right-2 px-2 py-0.5 text-xs font-medium bg-success-500 text-white rounded-full">
                Empfohlen
              </span>
            )}
            <div className="text-2xl mb-2">{strategy.icon}</div>
            <div className="font-semibold text-gray-800 dark:text-gray-200">
              {strategy.label}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {strategy.description}
            </div>
          </button>
        ))}
      </div>
      
      {/* Block Size (only for BLOCKS strategy) */}
      {chunkingStrategy === CHUNKING_STRATEGIES.BLOCKS && (
        <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Seiten pro Block
          </label>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="2"
              max="20"
              value={localBlockSize}
              onChange={(e) => handleBlockSizeChange(parseInt(e.target.value))}
              className="flex-1"
            />
            <input
              type="number"
              min="2"
              max="50"
              value={localBlockSize}
              onChange={(e) => handleBlockSizeChange(Math.max(2, parseInt(e.target.value) || 2))}
              className="w-20 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-center"
            />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            Ergibt {Math.ceil(selectedContent.pages / localBlockSize)} Chunks mit je ~{estimateTokens(selectedContent.text / Math.ceil(selectedContent.pages / localBlockSize)).toLocaleString()} Tokens
          </p>
        </div>
      )}
      
      {/* Chunks Preview */}
      {chunks.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-semibold text-gray-800 dark:text-gray-200">
            Vorschau der Chunks ({chunks.length} Stück)
          </h3>
          
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {chunks.map((chunk, index) => (
              <ChunkPreview
                key={index}
                chunk={chunk}
                index={index}
                isExpanded={expandedChunk === index}
                onToggle={handleToggleExpand}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Token Warning */}
      {selectedContent.tokens > 100000 && (
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="font-medium text-yellow-800 dark:text-yellow-200">
                Großes Dokument erkannt
              </p>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                Bei über 100.000 Tokens empfehlen wir die "Blöcke"-Strategie für eine effiziente Verarbeitung.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Navigation */}
      <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onPrev}
          className="px-6 py-2.5 rounded-lg font-medium flex items-center gap-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 theme-transition"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Zurück
        </button>
        
        <button
          onClick={onNext}
          disabled={chunks.length === 0}
          className={`
            px-6 py-2.5 rounded-lg font-medium flex items-center gap-2 theme-transition
            ${chunks.length > 0
              ? 'bg-primary-500 hover:bg-primary-600 text-white'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          Weiter zur Generierung
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export default WizardChunking;
